import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import LogoTile from "../components/LogoTile";
import { useT } from "../i18n";
import type { Lang as LangCode } from "../i18n";
import rowingPhoto from "../assets/zones/rowing-logo.jpg";
import inbodyPhoto from "../assets/zones/inbody.jpg";
import clanPhoto from "../assets/tasks/logo-story.jpeg";

const LANG_OPTS: { code: LangCode; label: string }[] = [
  { code: "kk", label: "ҚА" },
  { code: "ru", label: "РУ" },
  { code: "en", label: "EN" },
];

// Фоновые картинки зон: положи файлы в src/assets/zones/ и укажи здесь, например
//   import zone1Bg from "../assets/zones/zone1.jpg";
// и ниже в ZONES: { ..., image: zone1Bg }. Пока image не задан — градиент-плейсхолдер.
const ZONE_PLACEHOLDERS = [
  "linear-gradient(150deg, #4A2FA8, #1E1533)",
  "linear-gradient(150deg, #1E5F4E, #10201C)",
  "linear-gradient(150deg, #8A4A1E, #251208)",
];

// Онбординг до регистрации: язык, зоны фестиваля с призовой выжимкой и CTA.
export default function Entry() {
  const nav = useNavigate();
  const { t, lang, setLang } = useT();

  const zones: { n: number; emoji: string; titleKey: string; descKey: string; noteKey: string; image?: string }[] = [
    { n: 1, emoji: "🚣", titleKey: "stations.rowing.title", descKey: "entry.zone1.desc", noteKey: "entry.zone1.note", image: rowingPhoto },
    { n: 2, emoji: "📊", titleKey: "stations.inbody.title", descKey: "entry.zone2.desc", noteKey: "entry.zone2.note", image: inbodyPhoto },
    { n: 3, emoji: "🛡️", titleKey: "stations.battle.title", descKey: "entry.zone3.desc", noteKey: "entry.zone3.note", image: clanPhoto },
  ];

  // Активная карточка слайдера — для точек-индикатора.
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const [activeZone, setActiveZone] = useState(0);
  function onSliderScroll() {
    const el = sliderRef.current;
    if (!el) return;
    const card = el.firstElementChild as HTMLElement | null;
    if (!card) return;
    const step = card.offsetWidth + 12; // ширина карточки + gap-3
    setActiveZone(Math.max(0, Math.min(zones.length - 1, Math.round(el.scrollLeft / step))));
  }

  return (
    <div className="min-h-dvh max-w-md mx-auto flex flex-col px-[22px] py-5">
      {/* Language switcher — единый сегмент-контрол с рамкой и иконкой-глобусом,
          чтобы читался как выбор языка даже на солнце (одиночные полупрозрачные
          пилюли на улице сливались с фоном). */}
      <div className="flex justify-end">
        <div className="flex items-center gap-1 rounded-full bg-white/[0.08] ring-1 ring-white/20 p-1 pl-3">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-white/80 shrink-0 mr-1" aria-hidden>
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
            <path d="M3.6 9h16.8M3.6 15h16.8M12 3a14.2 14.2 0 0 1 0 18M12 3a14.2 14.2 0 0 0 0 18" stroke="currentColor" strokeWidth="1.6" />
          </svg>
          {LANG_OPTS.map((opt) => (
            <button
              key={opt.code}
              type="button"
              onClick={() => setLang(opt.code)}
              className={`h-8 px-3 rounded-full text-[13px] font-bold transition-colors ${
                lang === opt.code
                  ? "bg-violet-grad text-white shadow-violet"
                  : "text-white/85 active:bg-white/10"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Logo + title */}
      <div className="flex flex-col items-center text-center mt-4 mb-6">
        <LogoTile size={72} radius={22} fontSize={28} shadow="lg" />
        <h1 className="mt-5 text-[26px] font-extrabold tracking-tight leading-tight">{t("entry.title")}</h1>
        <p className="mt-1.5 text-[14px] font-bold text-violet-lighter">{t("entry.dates")}</p>
        <p className="mt-1.5 text-[15px] text-muted2">{t("entry.subtitle")}</p>
      </div>

      {/* Zone cards — свайп-слайдер */}
      <div
        ref={sliderRef}
        onScroll={onSliderScroll}
        className="no-scrollbar -mx-[22px] px-[22px] flex gap-3 overflow-x-auto snap-x snap-mandatory"
      >
        {zones.map((z, i) => (
          <div
            key={z.n}
            className="relative snap-center shrink-0 w-[80%] h-[300px] rounded-[22px] overflow-hidden border border-line-2"
          >
            {/* Фон: картинка зоны или градиент-плейсхолдер с эмодзи-водяным знаком */}
            {z.image ? (
              <img src={z.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <>
                <div className="absolute inset-0" style={{ background: ZONE_PLACEHOLDERS[i] }} />
                <span className="absolute inset-0 flex items-center justify-center text-[110px] opacity-[0.18] select-none">
                  {z.emoji}
                </span>
              </>
            )}
            {/* Затемнение снизу для читаемости текста */}
            <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/90 via-black/45 to-transparent" />

            <span className="absolute top-4 left-4 text-[11px] font-bold text-white bg-white/[0.16] backdrop-blur px-2.5 py-1 rounded-lg whitespace-nowrap">
              {t("stations.zonePrefix", { n: z.n })}
            </span>

            <div className="absolute inset-x-0 bottom-0 p-4">
              <div className="text-[19px] font-extrabold text-white leading-tight">{t(z.titleKey)}</div>
              <p className="text-[13px] text-white/75 mt-1.5 leading-[1.45]">{t(z.descKey)}</p>
              <p className="text-[12px] text-gold-light font-bold mt-1.5 leading-[1.4]">{t(z.noteKey)}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Точки-индикатор */}
      <div className="flex justify-center gap-1.5 mt-3">
        {zones.map((z, i) => (
          <span
            key={z.n}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === activeZone ? "w-5 bg-violet-lighter" : "w-1.5 bg-white/20"
            }`}
          />
        ))}
      </div>

      {/* Hero's Week — every participant */}
      <div className="mt-3 rounded-[16px] border border-gold/40 bg-gold/[0.08] px-4 py-3.5 text-[13px] leading-[1.5] text-gold-light font-semibold">
        🎁 {t("entry.hw")}
      </div>

      <button
        type="button"
        onClick={() => nav("/prizes")}
        className="mt-3 self-center text-violet-lighter text-sm font-semibold active:opacity-80 transition-opacity"
      >
        {t("entry.prizesLink")} →
      </button>

      {/* CTA */}
      <div className="mt-auto pt-6 flex flex-col gap-3">
        <button
          type="button"
          onClick={() => nav("/register")}
          className="w-full h-14 rounded-[14px] bg-violet-grad text-white font-extrabold text-base active:opacity-90 transition-opacity shadow-violet-btn"
        >
          {t("entry.cta")}
        </button>
        <button
          type="button"
          onClick={() => nav("/login")}
          className="w-full h-12 rounded-[14px] bg-white/[0.06] text-white font-bold text-sm ring-1 ring-white/10 active:bg-white/10 transition-colors"
        >
          {t("entry.haveAccount")}
        </button>
      </div>
    </div>
  );
}
